import React, { useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { Calendar, Save, Trash2, Search, Download, X, FileSpreadsheet, Filter } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import {
  exportFutureRegistrationsToExcel,
  filterRegistrationsForExport,
  RegistrationExportFilters,
} from '../../utils/futureRegistrationsExport';

interface FutureRegistration {
  id: string;
  created_at: string;
  registration_year: string;
  consultant_name: string;
  student_name: string;
  department: string;
  contract_sent: boolean;
  tracking_system_added: boolean;
  tracking_pages_added: boolean;
  groups_added: boolean;
  student_file_opened: boolean;
  resources_sent: boolean;
  sent_to_application_office: boolean;
}

interface ChecklistField {
  key: keyof FutureRegistration;
  label: string;
  trueText: string;
  falseText: string;
}

interface ConsultantOption {
  id: string;
  name: string;
}

const checklistFields: ChecklistField[] = [
  { key: 'contract_sent', label: 'Sözleşme', trueText: 'İletildi', falseText: 'İletilmedi' },
  { key: 'tracking_system_added', label: 'Takip Sis.', trueText: 'Eklendi', falseText: 'Eklenmedi' },
  { key: 'tracking_pages_added', label: 'T.S.S. Eklenmesi', trueText: 'Eklendi', falseText: 'Eklenmedi' },
  { key: 'groups_added', label: 'Gruplar', trueText: 'Eklendi', falseText: 'Eklenmedi' },
  { key: 'student_file_opened', label: 'Dosya', trueText: 'Açıldı', falseText: 'Açılmadı' },
  { key: 'resources_sent', label: 'Kaynak', trueText: 'İletildi', falseText: 'İletilmedi' },
  { key: 'sent_to_application_office', label: 'Başvuru Ofisi', trueText: 'İletildi', falseText: 'İletilmedi' },
];

const DEFAULT_WIDTHS: Record<string, number> = {
  no: 50,
  consultant: 180,
  date: 140,
  student: 220,
  department: 200,
  contract_sent: 130,
  tracking_system_added: 140,
  tracking_pages_added: 160,
  groups_added: 140,
  student_file_opened: 150,
  resources_sent: 130,
  sent_to_application_office: 160,
};

const FutureRegistrations: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState('2026');
  const [registrations, setRegistrations] = useState<FutureRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingField, setUpdatingField] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [listStartDate, setListStartDate] = useState('');
  const [listEndDate, setListEndDate] = useState('');
  const [listConsultant, setListConsultant] = useState('all');
  const [consultants, setConsultants] = useState<ConsultantOption[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState<RegistrationExportFilters>({
    startDate: '',
    endDate: '',
    consultant: 'all',
    department: 'all',
    completion: 'all',
  });


  // Column Resizing State with Persistence
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('future_registrations_col_widths');
      if (saved) {
        try {
          return { ...DEFAULT_WIDTHS, ...JSON.parse(saved) };
        } catch (e) {
          console.error('Error parsing saved column widths', e);
        }
      }
    }
    return DEFAULT_WIDTHS;
  });

  const resizingRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  // New row state
  const [newRowData, setNewRowData] = useState({
    consultant_name: '',
    created_at: new Date().toISOString().split('T')[0],
    student_name: '',
    department: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  const years = ['2026', '2027', '2028', '2029', '2030'];

  useEffect(() => {
    fetchRegistrations(selectedYear);
  }, [selectedYear]);

  // Save widths to localStorage
  useEffect(() => {
    localStorage.setItem('future_registrations_col_widths', JSON.stringify(colWidths));
  }, [colWidths]);

  const getColWidth = (key: string) => {
    return colWidths[key] || DEFAULT_WIDTHS[key] || 120;
  };

  // Calculate total width to enforce table size
  const totalTableWidth = useMemo(() => {
    const trashColWidth = 48; // w-12 is 3rem = 48px
    const coreCols = getColWidth('no') + getColWidth('consultant') + getColWidth('date') + getColWidth('student') + getColWidth('department');
    const checkCols = checklistFields.reduce((acc, field) => acc + getColWidth(field.key as string), 0);
    return trashColWidth + coreCols + checkCols;
  }, [colWidths]);

  const handleMouseMove = useRef((e: MouseEvent) => {
    if (!resizingRef.current) return;
    const { key, startX, startWidth } = resizingRef.current;

    // Calculate delta
    const diff = e.clientX - startX;
    const newWidth = Math.max(80, startWidth + diff);

    setColWidths(prev => ({
      ...prev,
      [key]: newWidth
    }));
  }).current;

  const handleMouseUp = useRef(() => {
    resizingRef.current = null;
    document.body.style.cursor = 'default';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }).current;

  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();

    resizingRef.current = {
      key,
      startX: e.clientX,
      startWidth: getColWidth(key)
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Filter registrations based on search, date range and consultant.
  const filteredRegistrations = useMemo(() => {
    const newestFirst = [...registrations].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const lowerTerm = searchTerm.toLowerCase();
    return newestFirst.filter((registration) => {
      const registrationDate = registration.created_at?.slice(0, 10) || '';
      const matchesSearch = !lowerTerm ||
        (registration.student_name?.toLowerCase() || '').includes(lowerTerm) ||
        (registration.consultant_name?.toLowerCase() || '').includes(lowerTerm) ||
        (registration.department?.toLowerCase() || '').includes(lowerTerm);
      const matchesConsultant = listConsultant === 'all' || registration.consultant_name === listConsultant;
      const matchesStartDate = !listStartDate || registrationDate >= listStartDate;
      const matchesEndDate = !listEndDate || registrationDate <= listEndDate;

      return matchesSearch && matchesConsultant && matchesStartDate && matchesEndDate;
    });
  }, [registrations, searchTerm, listConsultant, listStartDate, listEndDate]);

  const hasActiveListFilters = Boolean(searchTerm.trim() || listStartDate || listEndDate || listConsultant !== 'all');

  const clearListFilters = () => {
    setSearchTerm('');
    setListStartDate('');
    setListEndDate('');
    setListConsultant('all');
  };

  const exportableRegistrations = useMemo(
    () => filterRegistrationsForExport(filteredRegistrations, exportFilters),
    [filteredRegistrations, exportFilters],
  );

  const departmentOptions = useMemo(
    () => Array.from(new Set(registrations.map((registration) => registration.department).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'tr')),
    [registrations],
  );

  const consultantOptions = useMemo(
    () => Array.from(new Set(registrations.map((registration) => registration.consultant_name).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'tr')),
    [registrations],
  );

  const handleExport = () => {
    if (exportFilters.startDate && exportFilters.endDate && exportFilters.startDate > exportFilters.endDate) {
      alert('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    if (exportableRegistrations.length === 0) {
      alert('Seçtiğiniz ölçütlere uygun dışa aktarılacak kayıt bulunamadı.');
      return;
    }
    exportFutureRegistrationsToExcel(exportableRegistrations, selectedYear, exportFilters);
    setShowExportModal(false);
  };

  const { user } = useAuth();
  const [userBranchId, setUserBranchId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.branchId) {
      setUserBranchId(user.branchId);
    } else if (user?.id) {
      // Fallback fetch if branch_id is missing on user object context
      supabase.from('profiles').select('branch_id').eq('id', user.id).single()
        .then(({ data }) => {
          if (data) setUserBranchId(data.branch_id);
        });
    }
  }, [user]);

  const fetchRegistrations = async (year: string) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('future_registrations')
        .select('*')
        .eq('registration_year', year)
        .order('created_at', { ascending: false });

      // If user is not admin, RLS will filter, but we can also be explicit
      if (user?.role !== 'admin' && userBranchId) {
        query = query.eq('branch_id', userBranchId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setRegistrations(data || []);
    } catch (err: any) {
      console.error('Error fetching registrations:', err);
      if (err.code !== '42P01') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchConsultants = async () => {
      if (!userBranchId) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name')
          .eq('branch_id', userBranchId)
          .eq('unit_type', 'sales')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        setConsultants(data || []);
      } catch (err) {
        console.error('Error fetching consultants:', err);
      }
    };

    fetchConsultants();
  }, [userBranchId]);

  const handleToggleField = async (id: string, field: string, currentValue: boolean) => {
    const fieldKey = `${id}-${field}`;
    setUpdatingField(fieldKey);

    const newValue = !currentValue;

    setRegistrations((prev) =>
      prev.map((reg) =>
        reg.id === id ? { ...reg, [field]: newValue } : reg
      )
    );

    try {
      const { error } = await supabase
        .from('future_registrations')
        .update({ [field]: newValue })
        .eq('id', id);

      if (error) throw error;
    } catch (err: any) {
      console.error('Error updating field:', err);
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === id ? { ...reg, [field]: currentValue } : reg
        )
      );
      alert('Güncelleme hatası: ' + err.message);
    } finally {
      setUpdatingField(null);
    }
  };

  const handleUpdateField = async (id: string, field: string, value: any) => {
    setRegistrations((prev) =>
      prev.map((reg) =>
        reg.id === id ? { ...reg, [field]: value } : reg
      )
    );

    try {
      const { error } = await supabase
        .from('future_registrations')
        .update({ [field]: value })
        .eq('id', id);

      if (error) throw error;
    } catch (err: any) {
      console.error('Error updating field:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('future_registrations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRegistrations(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      console.error('Error deleting:', err);
      alert('Silme hatası: ' + err.message);
    }
  };

  const createNewRegistration = async () => {
    if (!newRowData.student_name) return;
    if (isCreating) return;

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('future_registrations')
        .insert({
          registration_year: selectedYear,
          consultant_name: newRowData.consultant_name,
          created_at: newRowData.created_at ? new Date(newRowData.created_at).toISOString() : new Date().toISOString(),
          student_name: newRowData.student_name,
          department: newRowData.department,
          branch_id: userBranchId
        })
        .select()
        .single();

      if (error) throw error;

      setRegistrations((current) => [data, ...current]);
      setNewRowData({
        consultant_name: '',
        created_at: new Date().toISOString().split('T')[0],
        student_name: '',
        department: ''
      });

    } catch (err: any) {
      console.error('Error creating registration:', err);
      alert('Kayıt oluşturulurken hata: ' + err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const renderToggleButton = (reg: FutureRegistration, field: ChecklistField) => {
    const isChecked = reg[field.key] as boolean;
    const isUpdating = updatingField === `${reg.id}-${field.key}`;

    return (
      <button
        onClick={() => handleToggleField(reg.id, field.key as string, isChecked || false)}
        disabled={isUpdating}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 w-full ${isChecked
          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60'
          : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60'
          } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {isChecked ? field.trueText : field.falseText}
      </button>
    );
  };

  const canSave = newRowData.student_name.length > 0;

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-6 flex-shrink-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-600" />
              Kayıtlar
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Yıllara göre manuel kayıt listesi ve takip sistemi
            </p>

          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Öğrenci, Danışman veya Bölüm Ara..."
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:w-64 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setExportFilters((current) => ({
                  ...current,
                  startDate: listStartDate,
                  endDate: listEndDate,
                  consultant: listConsultant,
                }));
                setShowExportModal(true);
              }}
              disabled={loading || registrations.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Download size={18} />
              Excel'e Aktar
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap flex-shrink-0">
        {years.map((year) => (
          <button
            key={year}
            onClick={() => setSelectedYear(year)}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${selectedYear === year
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {year}
          </button>
        ))}
      </div>

      <div className="mb-6 grid flex-shrink-0 grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:grid-cols-3 xl:grid-cols-[1fr_1fr_1.2fr_auto]">
        <div>
          <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <Calendar className="h-4 w-4 text-blue-600" /> Başlangıç tarihi
          </label>
          <input
            type="date"
            value={listStartDate}
            max={listEndDate || undefined}
            onChange={(event) => setListStartDate(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <Calendar className="h-4 w-4 text-blue-600" /> Bitiş tarihi
          </label>
          <input
            type="date"
            value={listEndDate}
            min={listStartDate || undefined}
            onChange={(event) => setListEndDate(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-blue-600" /> Danışman
          </label>
          <select
            value={listConsultant}
            onChange={(event) => setListConsultant(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          >
            <option value="all">Tüm danışmanlar</option>
            {consultantOptions.map((consultant) => (
              <option key={consultant} value={consultant}>{consultant}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={clearListFilters}
            disabled={!hasActiveListFilters}
            className="w-full whitespace-nowrap rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 xl:w-auto"
          >
            Filtreleri Temizle
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="overflow-auto flex-1 w-full relative max-h-[calc(100vh-280px)]">
            <table
              className="table-fixed divide-y divide-gray-200 dark:divide-gray-700"
              style={{ width: `${totalTableWidth}px` }}
            >
              <colgroup>
                <col style={{ width: '48px' }} />
                <col style={{ width: `${getColWidth('no')}px` }} />
                <col style={{ width: `${getColWidth('consultant')}px` }} />
                <col style={{ width: `${getColWidth('date')}px` }} />
                <col style={{ width: `${getColWidth('student')}px` }} />
                <col style={{ width: `${getColWidth('department')}px` }} />
                {checklistFields.map(field => (
                  <col key={field.key} style={{ width: `${getColWidth(field.key as string)}px` }} />
                ))}
              </colgroup>
              <thead className="sticky top-0 z-30 bg-gray-50 dark:bg-gray-700 shadow-sm">
                <tr>
                  <th className="px-2 sticky top-0 left-0 z-40 bg-gray-50 dark:bg-gray-700"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <div className="flex items-center h-full">NO.</div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <div className="flex items-center h-full">DANIŞMAN</div>
                    <div className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-blue-300/50 transition-colors z-20" onMouseDown={(e) => startResize(e, 'consultant')} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <div className="flex items-center h-full">TARİH</div>
                    <div className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-blue-300/50 transition-colors z-20" onMouseDown={(e) => startResize(e, 'date')} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <div className="flex items-center h-full">ÖĞRENCİ</div>
                    <div className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-blue-300/50 transition-colors z-20" onMouseDown={(e) => startResize(e, 'student')} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <div className="flex items-center h-full">BÖLÜM</div>
                    <div className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-blue-300/50 transition-colors z-20" onMouseDown={(e) => startResize(e, 'department')} />
                  </th>
                  {checklistFields.map((field) => (
                    <th key={field.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap relative group select-none sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                      <div className="flex items-center h-full justify-center text-center w-full">{field.label.toUpperCase()}</div>
                      <div className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-blue-300/50 transition-colors z-20" onMouseDown={(e) => startResize(e, field.key as string)} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {/* NEW ENTRY ROW */}
                <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b-2 border-blue-100 dark:border-blue-800">
                  <td className="px-2 py-3 text-center sticky left-0 bg-white dark:bg-gray-800 z-10"></td>
                  <td className="px-4 py-3 text-center text-sm font-medium text-gray-400">
                    -
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ verticalAlign: 'middle' }}>
                    <select
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:border-blue-500 ring-0 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                      value={newRowData.consultant_name}
                      onChange={(e) => setNewRowData({ ...newRowData, consultant_name: e.target.value })}
                    >
                      <option value="">Danışman Seçiniz</option>
                      {consultants.map(c => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ verticalAlign: 'middle' }}>
                    <input
                      type="date"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:border-blue-500 ring-0 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                      value={newRowData.created_at}
                      onChange={(e) => setNewRowData({ ...newRowData, created_at: e.target.value })}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium" style={{ verticalAlign: 'middle' }}>
                    <input
                      type="text"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:border-blue-500 ring-0 focus:ring-2 focus:ring-blue-200 outline-none transition-all placeholder-gray-400"
                      placeholder="Öğrenci Adı Soyadı"
                      value={newRowData.student_name}
                      onChange={(e) => setNewRowData({ ...newRowData, student_name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && createNewRegistration()}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ verticalAlign: 'middle' }}>
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="text"
                        className="flex-1 min-w-[120px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:border-blue-500 ring-0 focus:ring-2 focus:ring-blue-200 outline-none transition-all placeholder-gray-400"
                        placeholder="Bölüm"
                        value={newRowData.department}
                        onChange={(e) => setNewRowData({ ...newRowData, department: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && createNewRegistration()}
                      />

                      {canSave && (
                        <button
                          onClick={createNewRegistration}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-xs font-medium whitespace-nowrap shadow-sm animate-fade-in"
                        >
                          <Save size={14} />
                          Kaydet
                        </button>
                      )}
                    </div>
                  </td>
                  <td colSpan={checklistFields.length} className="px-4 py-3 text-center text-xs text-gray-400 italic bg-gray-50/50 dark:bg-gray-800/50" style={{ verticalAlign: 'middle' }}>
                    Kaydedildikten sonra butonlar açılacaktır
                  </td>
                </tr>

                {filteredRegistrations.map((reg, index) => (
                  <tr
                    key={reg.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-2 py-3 text-center sticky left-0 bg-white dark:bg-gray-800 z-10">
                      <button
                        onClick={() => handleDelete(reg.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-medium text-center" style={{ verticalAlign: 'middle' }}>
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate" style={{ verticalAlign: 'middle' }}>
                      <select
                        className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 px-0 py-1 transition-colors"
                        value={reg.consultant_name || ''}
                        onChange={(e) => handleUpdateField(reg.id, 'consultant_name', e.target.value)}
                      >
                        <option value="">Seçiniz</option>
                        {consultants.map(c => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate" style={{ verticalAlign: 'middle' }}>
                      <input
                        type="date"
                        className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 px-0 py-1 transition-colors"
                        value={reg.created_at ? new Date(reg.created_at).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleUpdateField(reg.id, 'created_at', new Date(e.target.value).toISOString())}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium truncate" style={{ verticalAlign: 'middle' }}>
                      <input
                        type="text"
                        className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 px-0 py-1 transition-colors font-bold"
                        value={reg.student_name || ''}
                        onChange={(e) => handleUpdateField(reg.id, 'student_name', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate" style={{ verticalAlign: 'middle' }}>
                      <input
                        type="text"
                        className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 px-0 py-1 transition-colors"
                        value={reg.department || ''}
                        onChange={(e) => handleUpdateField(reg.id, 'department', e.target.value)}
                      />
                    </td>
                    {checklistFields.map((field) => (
                      <td key={field.key as string} className="px-4 py-3 text-center" style={{ verticalAlign: 'middle' }}>
                        {renderToggleButton(reg, field)}
                      </td>
                    ))}
                  </tr>
                ))}

              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">
        Toplam {filteredRegistrations.length} kayıt
      </div>

      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={() => setShowExportModal(false)}>
          <div
            className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-title"
          >
            <div className="flex items-start justify-between border-b border-gray-200 dark:border-gray-700 p-6">
              <div className="flex gap-3">
                <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/40 p-3 text-emerald-700 dark:text-emerald-300">
                  <FileSpreadsheet size={24} />
                </div>
                <div>
                  <h2 id="export-title" className="text-xl font-bold text-gray-900 dark:text-white">Kayıtları Excel'e Aktar</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Dışa aktarılacak kayıtların ölçütlerini seçin.</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowExportModal(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white" aria-label="Pencereyi kapat">
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Kayıt yılı</label>
                <input value={selectedYear} disabled className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Takip durumu</label>
                <select value={exportFilters.completion} onChange={(e) => setExportFilters((current) => ({ ...current, completion: e.target.value as RegistrationExportFilters['completion'] }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  <option value="all">Tüm kayıtlar</option>
                  <option value="complete">Tüm işlemleri tamamlananlar</option>
                  <option value="incomplete">Eksik işlemi olanlar</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Başlangıç tarihi</label>
                <input type="date" value={exportFilters.startDate} onChange={(e) => setExportFilters((current) => ({ ...current, startDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Bitiş tarihi</label>
                <input type="date" value={exportFilters.endDate} onChange={(e) => setExportFilters((current) => ({ ...current, endDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Danışman</label>
                <select value={exportFilters.consultant} onChange={(e) => setExportFilters((current) => ({ ...current, consultant: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  <option value="all">Tüm danışmanlar</option>
                  {consultantOptions.map((consultant) => <option key={consultant} value={consultant}>{consultant}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">Bölüm</label>
                <select value={exportFilters.department} onChange={(e) => setExportFilters((current) => ({ ...current, department: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  <option value="all">Tüm bölümler</option>
                  {departmentOptions.map((department) => <option key={department} value={department}>{department}</option>)}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800/80 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-bold text-gray-900 dark:text-white">{exportableRegistrations.length}</span> kayıt aktarılacak
                {searchTerm.trim() && <span className="block text-xs text-gray-500">Mevcut arama filtresi de uygulanıyor: “{searchTerm}”</span>}
                {(listStartDate || listEndDate || listConsultant !== 'all') && (
                  <span className="block text-xs text-gray-500">Sayfadaki tarih ve danışman filtreleri çıktıya uygulanıyor.</span>
                )}
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowExportModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">Vazgeç</button>
                <button type="button" onClick={handleExport} disabled={exportableRegistrations.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <Download size={18} /> Excel Dosyasını İndir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default FutureRegistrations;
